import { observable, action, runInAction, computed, toJS, makeObservable, reaction } from "mobx";
import { CoreStore, handleStoreState } from "Stores/RESTStore/CoreStore";
import { API_URL } from "General/APIEndpoints";
import { projectsDataStore } from "Stores";
import { API } from "General/API";
import { isEmpty } from "utils/isEmpty";
import { download } from "utils/download";
import { actionProgressStore } from "Components/ActionProgress/ActionProgressStore";
import { MessageType } from "antd/es/message/interface";
import { uniqBy } from "lodash";
import { documentsTableStore } from "../Stores/DocumentsTableStore";
import i18n, { I18N_NS } from "Config/i18n";

export type AdditionalOptions = keyof Omit<API_DownloadWizardProperties, "fileIds">;
export type LedIndicatorsOptions = keyof Omit<API_DownloadWizardRequest, "files">;
export type DownloadMode = "RWM" | "ALL";

interface DownloadRequestQueueItem extends API_DownloadWizardProperties {
  downloadProgressId: string;
}

export class FileDownloadStore extends CoreStore {
  isVisible = false;
  initialData: API_DownloadWizardRequest = {};
  postFiles: number[] = [];
  downloadMode: DownloadMode = "ALL";
  ledOptions: {
    [key in LedIndicatorsOptions]?: boolean;
  } = {};
  postOptions: {
    [key in AdditionalOptions]?: boolean;
  } = {
    pdfWherePossible: false,
    withDirectories: false,
    withQR: false,
    withWatermark: false,
    withXfdfAnnotations: false,
  };
  activeSessions = 0; // amount of pending download requests
  filesQuery: DownloadRequestQueueItem[] = []; // buffer to store all requests somewhere;
  hide: MessageType;
  furtherOptionsDisabled: boolean = false;

  constructor() {
    super();

    makeObservable(this, {
      isVisible: observable,
      initialData: observable,
      postFiles: observable,
      downloadMode: observable,
      ledOptions: observable,
      postOptions: observable,
      activeSessions: observable,
      filesQuery: observable,
      furtherOptionsDisabled: observable,
      clear: action.bound,
      hideModal: action.bound,
      setMode: action.bound,
      setOptions: action.bound,
      setFiles: action.bound,
      selectedFiles: computed,
      removeFile: action.bound,
      files: computed,
      options: computed,
      initialCheckedFiles: computed,
      fetch: action.bound,
      initModal: action.bound,
      download: action.bound,
      requestDownload: action.bound,
    });
  }

  url() {
    return API_URL.TAGS.getDownloadWizardRequest(projectsDataStore.projectId);
  }

  clear() {
    this.isVisible = false;
    this.initialData = {};
    this.postFiles = [];
    this.ledOptions = {};
    this.furtherOptionsDisabled = false;

    this.postOptions = {
      pdfWherePossible: false,
      withDirectories: false,
      withQR: false,
      withWatermark: false,
    };
  }

  @handleStoreState()
  async initModal(files: API_FileRequest[], mode: "RWM" | "ALL" = "ALL") {
    this.setMode(mode);
    this.postOptions.withQR = projectsDataStore.isQRForce();
    this.isVisible = true;
    this.furtherOptionsDisabled = false;
    try {
      await this.fetch(files);
    } catch (error) {
      throw error;
    }
  }

  hideModal() {
    this.isVisible = false;
    this.clear();
  }

  @handleStoreState()
  async fetch(files: API_FileRequest[]) {
    try {
      const formData: API_MultipleFileRequest = { files };
      const response: API_DownloadWizardRequest = await API.newData(this.url(), formData);
      runInAction(() => {
        this.initialData = response;
        this.ledOptions = this.options;
        this.furtherOptionsDisabled = response.files.some((f) => f.state === "EXTERN_SYMLINK");
        this.setFiles(response.files.map((item) => item.id));
      });
    } catch (error) {
      throw error;
    }
  }

  @handleStoreState()
  download(fileIds: number[] = this.postFiles) {
    const mergedPostOptions =
      this.downloadMode === "RWM"
        ? { ...this.postOptions, pdfWherePossible: true, withWatermark: true }
        : this.postOptions;

    const files: API_DownloadWizardProperties["files"] = fileIds.map((id) => {
      const fileFound = this.initialData.files.find((item) => item.id === id) || ({} as API_DownloadWizardRequestFile);
      if (fileFound.state === "SYMLINK") {
        return { id: fileFound.linkId, state: "SYMLINK" };
      } else if (fileFound.state === "EXTERN_SYMLINK") {
        return { id: fileFound.id, state: "EXTERN_SYMLINK", linkId: fileFound.linkId };
      } else {
        return { id: id, state: fileFound.state };
      }
    });
    const formBody: API_DownloadWizardProperties = {
      ...mergedPostOptions,
      files: files,
    };
    this.isVisible = false;
    const downloadProgressId = actionProgressStore.init(
      this.activeSessions > 0 ? i18n.t("Waiting for former download to finish...", { ns: I18N_NS.PROJECT }) : undefined
    );
    this.filesQuery.push({ ...formBody, downloadProgressId });
  }

  // Adds download job to the queue
  submitDownloadJob(formData: API_DownloadWizardProperties) {
    let initialMessage: string;
    if (formData.withWatermark) {
      // generating watermarks is sometimes slow - we show message on top so that user knew what he's waiting for
      initialMessage = i18n.t("Adding watermarks...", { ns: I18N_NS.PROJECT });
    } else if (this.activeSessions > 0) {
      // if download job is added while another is still in progress - show message
      // "Waiting for former download to finish..." instead of the progress bar
      initialMessage = i18n.t("Waiting for former download to finish...", { ns: I18N_NS.PROJECT });
    }
    const downloadProgressId = actionProgressStore.init(initialMessage);
    this.filesQuery.push({ ...formData, downloadProgressId });
  }

  async requestDownload(formBody: API_DownloadWizardProperties, downloadProgressId: string) {
    try {
      this.activeSessions++;
      const cancelToken = actionProgressStore.getCancelToken(downloadProgressId);

      const file: File = await API.getBlobByPost(
        API_URL.FILES.getDownloadWizard2Response(projectsDataStore.projectId),
        formBody,
        null,
        {
          onDownloadProgress: (evt) => {
            if (this.hide && evt.loaded) {
              this.hide?.();
            }
            actionProgressStore.setProgress(downloadProgressId, evt);
          },
          cancelToken,
        }
      );

      runInAction(() => {
        const fileIds = formBody.files.map((item) => item.id);
        documentsTableStore.markFilesAsRead(fileIds.map(Number));
        download(file, file.name);
      });
    } catch (error) {
      throw error;
    } finally {
      this.activeSessions--;
      actionProgressStore.dismiss(downloadProgressId);
    }
  }

  setMode(mode: DownloadMode) {
    this.downloadMode = mode;
  }

  setOptions(option: AdditionalOptions) {
    this.postOptions[option] = !this.postOptions[option];
  }

  setFiles(ids: number[]) {
    this.postFiles = ids;
  }

  get selectedFiles() {
    const allFilesFlat = uniqBy(
      this.files.reduce((arr, current) => {
        arr.push(toJS(current));
        // "slaves" is not defined by back-end but still present in back-end response...
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        if ((current as any).slaves) {
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          arr = arr.concat((current as any).slaves.slice());
        }
        return arr;
      }, []),
      "id"
    );
    return this.postFiles.map((id) => allFilesFlat.find((file) => file.id === +id) || { id: +id });
  }

  removeFile(id: number) {
    this.initialData.files = this.initialData.files.filter((item) => item.id !== id);
  }

  downloadNextFile = () => {
    const { downloadProgressId, ...formBody } = this.filesQuery[0];
    this.requestDownload(formBody, downloadProgressId);
    this.filesQuery.shift();
  };

  cancelDownloadJob(downloadProgressId: string | number) {
    this.filesQuery = this.filesQuery.filter((item) => item.downloadProgressId !== downloadProgressId);
  }

  get files() {
    return Object.prototype.hasOwnProperty.call(this.initialData, "files") ? this.initialData.files : [];
  }

  get options() {
    if (!isEmpty(this.initialData)) {
      const pdfConversion = this.initialData.pdfConversion || false;
      const qrcode = this.initialData.qrcode || false;
      return {
        pdfConversion,
        qrcode,
      };
    }
    return null;
  }

  get initialCheckedFiles() {
    const checkedIds = [];

    const loop = (file) => {
      if (file instanceof Array) {
        file.forEach((item) => {
          return loop(item);
        });
      } else {
        file.checked && checkedIds.push(`${file.id}`);
        if (file.slaves && file.slaves.length) {
          return loop(file.slaves);
        }
      }
    };

    this.files.forEach((file) => {
      loop(file);
    });

    return checkedIds;
  }
}

export const fileDownloadStore = new FileDownloadStore();
/**
 * This section controls file upload process to handle maximum 5 active requests.
 * https://mobx.js.org/refguide/reaction.html
 */
reaction(
  () => ({
    count: fileDownloadStore.activeSessions,
    query: fileDownloadStore.filesQuery.length,
  }),
  ({ count, query }) => {
    if (count < 1 && query > 0) {
      fileDownloadStore.downloadNextFile();
    }
  }
);

if (process.env.NODE_ENV === "development") {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  (window as any).fileDownload = fileDownloadStore;
}
